class PehCalendar extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    const source = this.querySelector('[data-calendar-events]');
    this.grid = this.querySelector('[data-calendar-grid]');
    this.mobileList = this.querySelector('[data-calendar-mobile-list]');
    this.label = this.querySelector('[data-calendar-label]');
    this.empty = this.querySelector('[data-calendar-empty]');

    try {
      this.events = JSON.parse(source?.textContent || '[]').filter((event) => event.date);
    } catch (_error) {
      this.events = [];
    }

    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const futureEventMonths = this.events
      .map((event) => new Date(`${event.date}T12:00:00`))
      .filter((date) => !Number.isNaN(date.getTime()) && date >= currentMonth)
      .sort((a, b) => a - b);
    const initialDate = futureEventMonths[0] || currentMonth;
    this.visibleMonth = new Date(initialDate.getFullYear(), initialDate.getMonth(), 1);

    this.querySelector('[data-calendar-previous]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-calendar-next]')?.addEventListener('click', () => this.move(1));
    this.render();
  }

  move(delta) {
    this.visibleMonth = new Date(
      this.visibleMonth.getFullYear(),
      this.visibleMonth.getMonth() + delta,
      1
    );
    this.render();
  }

  render() {
    const year = this.visibleMonth.getFullYear();
    const month = this.visibleMonth.getMonth();
    const monthLabel = new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' }).format(this.visibleMonth);
    this.label.textContent = monthLabel;

    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthEvents = this.events.filter((event) => {
      const date = new Date(`${event.date}T12:00:00`);
      return date.getFullYear() === year && date.getMonth() === month;
    });

    const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
    const cells = dayNames.map((name) => `<div class="peh-calendar__weekday" aria-hidden="true">${name}</div>`);

    for (let index = 0; index < firstWeekday; index += 1) {
      cells.push('<div class="peh-calendar__day peh-calendar__day--empty" aria-hidden="true"></div>');
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const events = monthEvents.filter((event) => event.date === iso);
      const eventMarkup = events.map((event) => this.eventLink(event)).join('');
      cells.push(
        `<div class="peh-calendar__day${events.length ? ' peh-calendar__day--active' : ''}">` +
          `<time datetime="${iso}" class="peh-calendar__date">${day}</time>${eventMarkup}</div>`
      );
    }

    this.grid.innerHTML = cells.join('');
    this.mobileList.innerHTML = monthEvents
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((event) => `<li>${this.eventLink(event, true)}</li>`)
      .join('');
    this.empty.hidden = monthEvents.length !== 0;
  }

  eventLink(event, expanded = false) {
    const date = new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'long' }).format(new Date(`${event.date}T12:00:00`));
    const time = event.time ? `<span>${this.escape(event.time)}</span>` : '';
    const audience = event.audience
      ? `<span class="peh-taxonomy peh-taxonomy--${this.slug(event.audience)}">${this.escape(event.audience)}</span>`
      : '';
    const age = event.age ? `<span class="peh-age-range">${this.escape(event.age)}</span>` : '';
    const venue = event.venue ? `<span class="peh-calendar__venue">${this.escape(event.venue)}</span>` : '';
    const publicStatus = String(event.status || '').toLowerCase() === 'vendite aperte'
      ? 'BIGLIETTI DISPONIBILI'
      : event.status;
    const status = publicStatus ? `<span class="peh-status">${this.escape(publicStatus)}</span>` : '';
    const dateText = expanded
      ? `<time datetime="${event.date}">${date}${event.time ? ` · ${this.escape(event.time)}` : ''}</time>`
      : time;
    return `<a class="peh-calendar__event" href="${this.escape(event.url)}">${dateText}<strong>${this.escape(event.title)}</strong><span class="peh-event-taxonomy">${audience}${age}</span>${venue}${status}</a>`;
  }

  slug(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  escape(value) {
    const node = document.createElement('span');
    node.textContent = String(value || '');
    return node.innerHTML;
  }
}

if (!customElements.get('peh-calendar')) customElements.define('peh-calendar', PehCalendar);
